import type {OutlookRecipient} from './EmailScreen/Content/EmailRecipients/useOutlookEmailRecipients';
import type {ComposeMessageMetadata} from './sendDialogProtocol';
import type {MessageAttachment} from './SendModal/EmailSync/structs';

export const LOXO_EMAIL_SYNC_APPROVAL_KEY = 'loxoEmailSyncApprovedMessage';
export const LOXO_EMAIL_SYNC_SMART_ALERT_ACTION = 'openSendReview';
const LOXO_OUTLOOK_EMAIL_SYNC_HEADER = 'X-Loxo-Outlook-Email-Sync';
const LOXO_OUTLOOK_DO_NOT_SYNC_HEADER = 'X-Loxo-Outlook-Do-Not-Sync';
const APPROVAL_TTL_MS = 5 * 60 * 1000;

export type EmailSyncApproval = ComposeMessageMetadata & {
  approvedAt: number;
};

const normalizeEmail = (email: string): string => email.trim().toLowerCase();

const asEmailString = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

const normalizeRecipient = (
  raw: any,
  field: OutlookRecipient['field']
): OutlookRecipient | null => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const email =
    asEmailString(raw.emailAddress) ?? asEmailString(raw.address) ?? null;
  if (!email) {
    return null;
  }

  const name = asEmailString(raw.displayName || raw.name) ?? undefined;
  return {email, name, field};
};

const dedupeRecipients = (
  recipients: OutlookRecipient[]
): OutlookRecipient[] => {
  const seen = new Set<string>();
  const out: OutlookRecipient[] = [];
  for (const recipient of recipients) {
    const key = `${recipient.field}:${normalizeEmail(recipient.email)}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    out.push(recipient);
  }
  return out;
};

const getMailboxItem = (): any => {
  if (typeof Office === 'undefined') {
    return null;
  }
  return Office.context?.mailbox?.item ?? null;
};

const readRecipientsFromArray = (
  value: any,
  field: OutlookRecipient['field']
): OutlookRecipient[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((raw) => normalizeRecipient(raw, field))
    .filter((recipient): recipient is OutlookRecipient => recipient != null);
};

const getAsyncRecipients = (
  recipientsObj: any,
  field: OutlookRecipient['field']
): Promise<OutlookRecipient[]> => {
  if (!recipientsObj || typeof recipientsObj.getAsync !== 'function') {
    return Promise.resolve([]);
  }
  return new Promise((resolve) => {
    recipientsObj.getAsync((result: any) => {
      resolve(readRecipientsFromArray(result?.value, field));
    });
  });
};

const readComposeMessageRecipients = async (): Promise<OutlookRecipient[]> => {
  const item = getMailboxItem();
  if (!item) {
    return [];
  }

  if (
    Array.isArray(item.to) ||
    Array.isArray(item.cc) ||
    Array.isArray(item.bcc)
  ) {
    return dedupeRecipients([
      ...readRecipientsFromArray(item.to, 'to'),
      ...readRecipientsFromArray(item.cc, 'cc'),
      ...readRecipientsFromArray(item.bcc, 'bcc'),
    ]);
  }

  const [to, cc, bcc] = await Promise.all([
    getAsyncRecipients(item.to, 'to'),
    getAsyncRecipients(item.cc, 'cc'),
    getAsyncRecipients(item.bcc, 'bcc'),
  ]);
  return dedupeRecipients([...to, ...cc, ...bcc]);
};

const readComposeMessageFromAddress = (): Promise<string> => {
  const item = getMailboxItem();
  if (!item) {
    return Promise.resolve('');
  }

  const direct = item.from;
  if (direct?.emailAddress && typeof direct.emailAddress === 'string') {
    return Promise.resolve(direct.emailAddress.trim());
  }

  if (item.from && typeof item.from.getAsync === 'function') {
    return new Promise((resolve) => {
      item.from.getAsync((result: any) => {
        if (
          result.status === Office.AsyncResultStatus.Succeeded &&
          result.value?.emailAddress
        ) {
          resolve(result.value.emailAddress.trim());
          return;
        }
        const profileEmail = Office.context?.mailbox?.userProfile?.emailAddress;
        resolve(typeof profileEmail === 'string' ? profileEmail.trim() : '');
      });
    });
  }

  const profileEmail = Office.context?.mailbox?.userProfile?.emailAddress;
  return Promise.resolve(
    typeof profileEmail === 'string' ? profileEmail.trim() : ''
  );
};

const readComposeMessageSubject = (): Promise<string> => {
  const item = getMailboxItem();
  if (!item) {
    return Promise.resolve('');
  }

  const subject = item.subject;
  if (typeof subject === 'string') {
    return Promise.resolve(subject.trim());
  }

  if (subject && typeof subject.getAsync === 'function') {
    return new Promise((resolve) => {
      subject.getAsync((result: any) => {
        if (
          result.status === Office.AsyncResultStatus.Succeeded &&
          typeof result.value === 'string'
        ) {
          resolve(result.value.trim());
          return;
        }
        resolve('');
      });
    });
  }

  return Promise.resolve('');
};

export const readComposeMessageMetadata =
  async (): Promise<ComposeMessageMetadata> => {
    const [fromAddress, recipients, subject] = await Promise.all([
      readComposeMessageFromAddress(),
      readComposeMessageRecipients(),
      readComposeMessageSubject(),
    ]);
    return {fromAddress, recipients, subject};
  };

export const EXTENSION_CONTENT_TYPES: Record<string, string> = {
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  txt: 'text/plain',
  csv: 'text/csv',
  html: 'text/html',
  htm: 'text/html',
  json: 'application/json',
  xml: 'application/xml',
  zip: 'application/zip',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
};

const guessContentTypeFromFileName = (fileName: string): string | null => {
  const extension = fileName.split('.').pop()?.toLowerCase();
  if (!extension) {
    return null;
  }
  return EXTENSION_CONTENT_TYPES[extension] ?? null;
};

const normalizeComposeAttachment = (raw: any): MessageAttachment | null => {
  const name = raw.name?.trim();
  const id = raw.id?.trim() || name;
  if (!name || !id) {
    return null;
  }
  if (
    raw.isInline &&
    raw.attachmentType === Office.MailboxEnums.AttachmentType.Item
  ) {
    return null;
  }
  return {
    id,
    name,
    contentType: guessContentTypeFromFileName(name),
    size: raw.size ?? 0,
  };
};

export const readComposeAttachments = (): Promise<MessageAttachment[]> => {
  const item = getMailboxItem();
  if (!item) {
    return Promise.resolve([]);
  }

  if (Array.isArray(item.attachments) && item.attachments.length > 0) {
    return Promise.resolve(
      item.attachments
        .map(normalizeComposeAttachment)
        .filter(
          (
            attachment: MessageAttachment | null
          ): attachment is MessageAttachment => attachment != null
        )
    );
  }

  return new Promise((resolve) => {
    if (typeof item.getAttachmentsAsync !== 'function') {
      resolve([]);
      return;
    }
    item.getAttachmentsAsync((result: any) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        resolve([]);
        return;
      }
      resolve(
        (result.value ?? [])
          .map(normalizeComposeAttachment)
          .filter(
            (
              attachment: MessageAttachment | null
            ): attachment is MessageAttachment => attachment != null
          )
      );
    });
  });
};

export const setOutlookEmailSyncHeader = (headerId: string): Promise<void> => {
  const trimmed = headerId.trim();
  if (!trimmed) {
    return Promise.reject(new Error('Email sync header id is required'));
  }

  return new Promise((resolve, reject) => {
    const internetHeaders = getMailboxItem()?.internetHeaders;
    if (!internetHeaders || typeof internetHeaders.setAsync !== 'function') {
      reject(
        new Error('Internet headers are not supported in this Outlook version')
      );
      return;
    }

    if (typeof internetHeaders.removeAsync !== 'function') {
      reject(
        new Error(
          'Internet header removal is not supported in this Outlook version'
        )
      );
      return;
    }

    internetHeaders.removeAsync(
      [LOXO_OUTLOOK_DO_NOT_SYNC_HEADER],
      (removeResult: Office.AsyncResult<void>) => {
        if (removeResult.status !== Office.AsyncResultStatus.Succeeded) {
          reject(
            new Error(
              removeResult.error?.message ??
                'Failed to remove do not sync header'
            )
          );
          return;
        }

        internetHeaders.setAsync(
          {[LOXO_OUTLOOK_EMAIL_SYNC_HEADER]: trimmed},
          (setResult: Office.AsyncResult<void>) => {
            if (setResult.status === Office.AsyncResultStatus.Succeeded) {
              resolve();
              return;
            }
            reject(
              new Error(
                setResult.error?.message ?? 'Failed to set email sync header'
              )
            );
          }
        );
      }
    );
  });
};

const sortedApprovalEmails = (metadata: ComposeMessageMetadata): string[] =>
  metadata.recipients
    .map((recipient) => normalizeEmail(recipient.email))
    .sort();

export const buildEmailSyncApproval = (
  metadata: ComposeMessageMetadata
): EmailSyncApproval => ({
  ...metadata,
  recipients: metadata.recipients.map((recipient) => ({...recipient})),
  approvedAt: Date.now(),
});

export const approvalMatchesComposeMessage = (
  approval: EmailSyncApproval | null | undefined,
  metadata: ComposeMessageMetadata,
  now = Date.now()
): boolean => {
  if (!approval || now - approval.approvedAt > APPROVAL_TTL_MS) {
    return false;
  }

  if (
    approval.subject !== metadata.subject ||
    normalizeEmail(approval.fromAddress) !==
      normalizeEmail(metadata.fromAddress)
  ) {
    return false;
  }

  return (
    sortedApprovalEmails(approval).join('|') ===
    sortedApprovalEmails(metadata).join('|')
  );
};

export const markCurrentMessageApprovedForSend = async (): Promise<void> => {
  const settings = Office.context?.roamingSettings;
  if (!settings) {
    throw new Error('Roaming settings are not available.');
  }

  const metadata = await readComposeMessageMetadata();
  settings.set(LOXO_EMAIL_SYNC_APPROVAL_KEY, buildEmailSyncApproval(metadata));
  await new Promise<void>((resolve, reject) => {
    settings.saveAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve();
        return;
      }
      reject(new Error(result.error?.message ?? 'Failed to save approval.'));
    });
  });
};

export const sendCurrentMessage = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    const item = getMailboxItem();
    const sendAsync = item?.sendAsync;
    if (typeof sendAsync !== 'function') {
      reject(new Error('Sending from the taskpane is not supported here.'));
      return;
    }

    sendAsync.call(item, (result: Office.AsyncResult<void>) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve();
        return;
      }
      reject(new Error(result.error?.message ?? 'Failed to send message.'));
    });
  });
};

export const readTaskpaneInitializationContext = (): Promise<unknown> => {
  return new Promise((resolve) => {
    const item = getMailboxItem();
    if (!item || typeof item.getInitializationContextAsync !== 'function') {
      resolve(null);
      return;
    }

    item.getInitializationContextAsync((result: Office.AsyncResult<string>) => {
      if (
        result.status !== Office.AsyncResultStatus.Succeeded ||
        typeof result.value !== 'string'
      ) {
        resolve(null);
        return;
      }

      try {
        resolve(JSON.parse(result.value));
      } catch {
        resolve(null);
      }
    });
  });
};
